import { useEffect, useState } from "react";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import ChartInfoTooltip from "components/Tooltip/ChartInfoTooltip";
import PropTypes from "prop-types";
import { axiosInstance } from "services";
import { chartTooltips } from "components/Tooltip/Definitions/data";

import StandardLoadingIndicator from "components/StandardLoadingIndicator";
import StandardMessageDisplay from "components/StandardMessageDisplay";

// Design tokens
import { GRADIENTS } from "utils/designTokens";
import GlassCard from "components/GlassCard";

function EffectivePublishingTimes({ companyId, limit = 5 }) {

  const [effectiveTimeSlots, setEffectiveTimeSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEffectiveTimes = async () => {
      if (!companyId) {
        return;
      }

      setError(null);

      try {
        const params = { companyId, limit };
        if (limit === 2) delete params.limit;

        const response = await axiosInstance.get(
          `/v1/frontend/publishing/effective-times`, { params },
        );

        const newData = response.data || [];
        setEffectiveTimeSlots(currentTimeSlots =>
          JSON.stringify(currentTimeSlots) !== JSON.stringify(newData) ? newData : currentTimeSlots,
        );
        setLoading(false);
      } catch (err) {
        console.error("Error fetching effective publishing times:", err);
        setError("Failed to load effective publishing times.");
        setEffectiveTimeSlots([]);
        setLoading(false);
      }
    };
    fetchEffectiveTimes();
  }, [companyId, limit]);

  return (
    <GlassCard sx={{ height: "100%" }}>
      <ChartInfoTooltip
        title="Most Effective Publishing Time"
        tooltipText={chartTooltips.effectivePublishingTimes}
      >
        {loading ? (
          <StandardLoadingIndicator containerHeight="190px" />
        ) : error ? (
          <StandardMessageDisplay
            type="error"
            containerHeight="190px"
          />
        ) : effectiveTimeSlots.length === 0 ? (
          <StandardMessageDisplay
            type="noData"
            title="No Effective Times"
            message="No specific effective publishing times were identified in the market during the chosen timeframe."
            containerHeight="190px"
          />
        ) : (
          <VuiBox sx={{
            overflowY: "auto",
            maxHeight: "200px",
            minHeight: "190px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 1.5,
          }}>
            {effectiveTimeSlots.map((slot, index) => (
              <VuiBox
                key={index}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={1.5}
                sx={{
                  background: "rgba(255, 255, 255, 0.05)",
                  borderRadius: "10px",
                  transform: "translateY(0)",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-3px)",
                  },
                }}
              >
                <VuiTypography variant="button" color="white" fontWeight="medium" sx={{ fontSize: "12px" }}>
                  {slot.weekday}, {slot.timeSlot}
                </VuiTypography>
                <VuiBox
                  sx={{
                    background: GRADIENTS.engagementBadge,
                    borderRadius: "5px",
                    px: 1,
                    py: 0.5,
                    minWidth: "100px",
                    textAlign: "center",
                  }}
                >
                  <VuiTypography variant="caption" color="white" fontWeight="bold" sx={{ fontSize: "12px" }}>
                    {slot.engagementRate}% Engagement
                  </VuiTypography>
                </VuiBox>
              </VuiBox>
            ))}
          </VuiBox>
        )}
      </ChartInfoTooltip>
    </GlassCard>
  );
}

EffectivePublishingTimes.propTypes = {
  companyId: PropTypes.string.isRequired,
  limit: PropTypes.number,
};

export default EffectivePublishingTimes;